import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Save, X,Eye, EyeOff, Copy, Check  } from "lucide-react";

interface LocationApiConfigModalProps {
  location?: {
    id: string;
    name: string;
    address: string;
  };
  user?: {
    id: string;
    title?: string;
    forename: string;
    surname: string;
    role: string;
    email: string;
  };
  app: {
    id: string;
    marketType: number;
    name: string;
    requirements: string;
  };
  currentApiKeys: {
    apiKey?: string;
    userId?: string;
    email?: string; 
    webhookUrl?: string;
    testMode?: boolean;
  };
  onSave: (apiKeys: any) => void;
  onClose: () => void;
  isDarkMode?: boolean;
}

export function LocationApiConfigModal({
  location,
  user,
  app,
  currentApiKeys,
  onSave,
  onClose,
  isDarkMode = false,
}: LocationApiConfigModalProps) {
  const [formData, setFormData] = useState({
    apiKey: currentApiKeys.apiKey || "",
    userId: currentApiKeys.userId || "",
    email: currentApiKeys.email || "",
    webhookUrl: currentApiKeys.webhookUrl || "",
    testMode: currentApiKeys.testMode ?? true
  });

  const [showApiKey, setShowApiKey] = React.useState(false);
  const [copied, setCopied] = useState(false);

  const updateField = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const payload: any = {
    apiKey: formData.apiKey,
    };

    if (app.marketType === 1) {
        payload.email = user?.email;
    }
    else {
        payload.userId = formData.userId;
        payload.webhookUrl = formData.webhookUrl;
        payload.testMode = formData.testMode;
    }

    onSave(payload);
  };

  const isFormValid = () => {
    // Basic validation - API key is required
    if (app.marketType === 1) {
    return formData.apiKey.trim() !== "" && user?.email.trim() !== "";
  }
    return formData.apiKey.trim() !== "";
  };

  const handleCopy = async () => {
  if (!formData.apiKey) return;

  await navigator.clipboard.writeText(formData.apiKey);
  setCopied(true);

  setTimeout(() => setCopied(false), 1500);
};


  const displayName = user 
    ? `${user.title || ""} ${user.forename} ${user.surname}`.trim()
    : location?.name || "";
  
  const displayDetail = user 
    ? `${user.email} • ${user.role}`
    : location?.address || "";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className={`max-w-lg w-full mx-4 rounded-lg p-6 transition-colors duration-300 ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3
              className={`text-lg font-semibold transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Configure {app.name} {app.marketType === 1 ? " Licenses" : "" }
            </h3>
            <p
              className={`text-sm transition-colors duration-300 ${
                isDarkMode ? "text-gray-300" : "text-gray-600"
              }`}
            >
              {displayName}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className={
              isDarkMode
                ? "text-gray-300 hover:text-white hover:bg-gray-700"
                : ""
            }
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Requirements Info */}
        <div
          className={`p-3 rounded-lg mb-6 transition-colors duration-300 ${
            isDarkMode
              ? "bg-blue-900/20 border border-blue-800"
              : "bg-blue-50 border border-blue-200"
          }`}
        >
          <p
            className={`text-sm transition-colors duration-300 ${
              isDarkMode ? "text-blue-300" : "text-blue-700"
            }`}
          >
            <strong>Requirements:</strong> {app.requirements}
          </p>
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="api-key">
                {app.marketType === 1 ? "License Key *" : "API Key *"}
              </Label>
                <div className="relative">
              <Input
                id="api-key"
                type={showApiKey ? "text" : "password"}
                value={formData.apiKey}
                onChange={(e) => updateField("apiKey", e.target.value)}
                placeholder={app.marketType === 1 ? "Enter License Key" : "Enter API key"}
                className={
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : ""
                }
              />
             
    <div className="absolute inset-y-0 right-0 flex items-center gap-2 pr-3">
      
      {/* Copy Button */}
      <button
        type="button"
        onClick={handleCopy}
        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
      >
        {copied ? <Check size={18} /> : <Copy size={18} />}
      </button>

      {/* Toggle Visibility */}
      <button
        type="button"
        onClick={() => setShowApiKey((prev) => !prev)}
        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
      >
        {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>

    </div>
            </div>
            </div>
            {app.marketType !== 1 && (
            <div className="space-y-2">
              <Label htmlFor="user-id">User ID</Label>
              <Input
                id="user-id"
                value={formData.userId}
                onChange={(e) => updateField("userId", e.target.value)}
                placeholder="Enter user ID"
                className={
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : ""
                }
              />
            </div>
            )}
          </div>
        {app.marketType !== 1 && (
          <>
          <div className="space-y-2">
            <Label htmlFor="webhook-url">Webhook URL</Label>
            <Input
              id="webhook-url"
              value={formData.webhookUrl}
              onChange={(e) => updateField("webhookUrl", e.target.value)}
              placeholder="https://your-domain.com/webhook"
              className={
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                  : ""
              }
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="test-mode"
              checked={formData.testMode}
              onCheckedChange={(checked) => updateField("testMode", checked)}
            />
            <Label htmlFor="test-mode">Enable test mode</Label>
          </div>
          </>
        )}
        </div>

        {/* Location Info */}
        <div
          className={`mt-6 p-3 rounded-lg border transition-colors duration-300 ${
            isDarkMode
              ? "border-gray-700 bg-gray-700"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <div
            className={`text-xs font-medium mb-1 transition-colors duration-300 ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            {user ? "Member Details" : "Store Location"}
          </div>
          <div
            className={`text-sm transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            {displayDetail}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Cancel
          </Button>
          <Button
            className="flex-1 bg-slate-900 hover:bg-slate-800"
            onClick={handleSubmit}
            disabled={!isFormValid()}
          >
            <Save className="w-4 h-4 mr-2" />
            Save Configuration
          </Button>
        </div>
      </div>
    </div>
  );
}
